export type PaperSize = "A4" | "A5" | "halfLetter";
export type LogoPosition = "right" | "left" | "hidden";

export type PatientReportTheme = {
  primary: string;
  accent: string;
  text: string;
  textMuted: string;
  border: string;
};

export type PatientFields = {
  name: boolean;
  gender: boolean;
  age: boolean;
  dob: boolean;
  reg: boolean;
  mobile: boolean;
  address: boolean;
};

export type TreatmentColumns = {
  no: boolean;
  date: boolean;
  treatment: boolean;
  tooth: boolean;
  qty: boolean;
  price: boolean;
  status: boolean;
  dentist: boolean;
  note: boolean;
};

export type PaymentColumns = {
  no: boolean;
  date: boolean;
  invoiceNo: boolean;
  total: boolean;
  discount: boolean;
  cash: boolean;
  card: boolean;
  transfer: boolean;
};

export type PatientReportPrint = {
  paperSize: PaperSize;
  sections: string[];
  logoPosition: LogoPosition;
  showAddress: boolean;
  showPhone: boolean;
  showGeneratedAt: boolean;
  showFooter: boolean;
  margin: number;
  theme: PatientReportTheme;
  patientFields: PatientFields;
  treatmentColumns: TreatmentColumns;
  paymentColumns: PaymentColumns;
};

export const REPORT_SECTIONS = [
  { KEY: "1", label: "Patient complain" },
  { KEY: "2", label: "Investigation" },
  { KEY: "3", label: "Diagnosis Chart" },
  { KEY: "7", label: "Diagnosis table" },
  { KEY: "4", label: "Treatments" },
  { KEY: "8", label: "Prescription" },
  { KEY: "5", label: "Payments" },
  { KEY: "6", label: "Medical History" },
] as const;

export const PAPER_SIZES: { value: PaperSize; label: string; hint: string }[] = [
  { value: "A4", label: "A4", hint: "210 × 297 mm" },
  { value: "A5", label: "A5", hint: "148 × 210 mm" },
  { value: "halfLetter", label: "Half Letter", hint: "5.5 × 8.5 in (140 × 216 mm)" },
];

export const PATIENT_FIELD_OPTIONS: { key: keyof PatientFields; label: string }[] = [
  { key: "name", label: "Name" },
  { key: "gender", label: "Gender" },
  { key: "age", label: "Age" },
  { key: "dob", label: "Date of birth" },
  { key: "reg", label: "Reg No" },
  { key: "mobile", label: "Mobile" },
  { key: "address", label: "Address" },
];

export const TREATMENT_COLUMN_OPTIONS: {
  key: keyof TreatmentColumns;
  label: string;
}[] = [
  { key: "no", label: "No" },
  { key: "date", label: "Date" },
  { key: "treatment", label: "Treatment" },
  { key: "tooth", label: "Tooth" },
  { key: "qty", label: "Qty" },
  { key: "price", label: "Price" },
  { key: "status", label: "Status" },
  { key: "dentist", label: "Dentist" },
  { key: "note", label: "Note" },
];

export const PAYMENT_COLUMN_OPTIONS: {
  key: keyof PaymentColumns;
  label: string;
}[] = [
  { key: "no", label: "No" },
  { key: "date", label: "Date" },
  { key: "invoiceNo", label: "Invoice no" },
  { key: "total", label: "Total" },
  { key: "discount", label: "Discount" },
  { key: "cash", label: "Cash" },
  { key: "card", label: "Card" },
  { key: "transfer", label: "Transfer" },
];

const VALID_SECTION_KEYS = REPORT_SECTIONS.map((item) => item.KEY);

function allTrue<T extends string>(keys: T[]): Record<T, boolean> {
  return keys.reduce((acc, key) => {
    acc[key] = true;
    return acc;
  }, {} as Record<T, boolean>);
}

export const DEFAULT_PATIENT_REPORT_PRINT: PatientReportPrint = {
  paperSize: "A4",
  sections: [...VALID_SECTION_KEYS],
  logoPosition: "right",
  showAddress: true,
  showPhone: true,
  showGeneratedAt: true,
  showFooter: true,
  margin: 36,
  theme: {
    primary: "#174d7a",
    accent: "#e8f4fc",
    text: "#1e293b",
    textMuted: "#64748b",
    border: "#e2e8f0",
  },
  patientFields: allTrue(PATIENT_FIELD_OPTIONS.map((item) => item.key)),
  treatmentColumns: allTrue(TREATMENT_COLUMN_OPTIONS.map((item) => item.key)),
  paymentColumns: allTrue(PAYMENT_COLUMN_OPTIONS.map((item) => item.key)),
};

function isHexColor(value: unknown): value is string {
  return typeof value === "string" && /^#([0-9a-fA-F]{6})$/.test(value.trim());
}

function bool(value: unknown, fallback: boolean) {
  if (value === false || value === "false" || value === 0 || value === "0") {
    return false;
  }
  if (value === true || value === "true" || value === 1 || value === "1") {
    return true;
  }
  return fallback;
}

function flagMap<T extends string>(
  raw: unknown,
  keys: T[]
): Record<T, boolean> {
  const source = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  return keys.reduce((acc, key) => {
    acc[key] = bool(source[key], true);
    return acc;
  }, {} as Record<T, boolean>);
}

export function normalizePatientReportPrint(raw: unknown): PatientReportPrint {
  const source = raw && typeof raw === "object" ? (raw as Record<string, any>) : {};
  const rawSize = String(source.paperSize || "A4").trim();
  const paperSize: PaperSize =
    rawSize === "halfLetter" || rawSize.toLowerCase() === "halfletter"
      ? "halfLetter"
      : rawSize.toUpperCase() === "A5"
        ? "A5"
        : "A4";
  const logoRaw = String(source.logoPosition || "right").toLowerCase();
  const logoPosition: LogoPosition =
    logoRaw === "left" || logoRaw === "hidden" ? logoRaw : "right";

  let sections: string[] = Array.isArray(source.sections)
    ? source.sections.map(String)
    : [];
  sections = sections.filter((key) => VALID_SECTION_KEYS.includes(key as any));
  if (!sections.length) sections = [...DEFAULT_PATIENT_REPORT_PRINT.sections];

  const themeSource = source.theme && typeof source.theme === "object" ? source.theme : {};
  const marginNum = Number(source.margin);
  const margin = Number.isFinite(marginNum)
    ? Math.max(20, Math.min(56, Math.round(marginNum)))
    : DEFAULT_PATIENT_REPORT_PRINT.margin;

  return {
    paperSize,
    sections,
    logoPosition,
    showAddress: bool(source.showAddress, true),
    showPhone: bool(source.showPhone, true),
    showGeneratedAt: bool(source.showGeneratedAt, true),
    showFooter: bool(source.showFooter, true),
    margin,
    theme: {
      primary: isHexColor(themeSource.primary)
        ? themeSource.primary.trim()
        : DEFAULT_PATIENT_REPORT_PRINT.theme.primary,
      accent: isHexColor(themeSource.accent)
        ? themeSource.accent.trim()
        : DEFAULT_PATIENT_REPORT_PRINT.theme.accent,
      text: isHexColor(themeSource.text)
        ? themeSource.text.trim()
        : DEFAULT_PATIENT_REPORT_PRINT.theme.text,
      textMuted: isHexColor(themeSource.textMuted)
        ? themeSource.textMuted.trim()
        : DEFAULT_PATIENT_REPORT_PRINT.theme.textMuted,
      border: isHexColor(themeSource.border)
        ? themeSource.border.trim()
        : DEFAULT_PATIENT_REPORT_PRINT.theme.border,
    },
    patientFields: flagMap(
      source.patientFields,
      PATIENT_FIELD_OPTIONS.map((item) => item.key)
    ),
    treatmentColumns: flagMap(
      source.treatmentColumns,
      TREATMENT_COLUMN_OPTIONS.map((item) => item.key)
    ),
    paymentColumns: flagMap(
      source.paymentColumns,
      PAYMENT_COLUMN_OPTIONS.map((item) => item.key)
    ),
  };
}
